/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Fullscreen as FullscreenIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

interface FullscreenTextAreaProps {
  title: string;
  content: string;
  maxHeight?: string;
  format?: 'text' | 'json';
}

const FullscreenTextArea: React.FC<FullscreenTextAreaProps> = ({
  title,
  content,
  maxHeight = '300px',
  format = 'text',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(content);
  };

  const handleFullscreen = () => {
    setFullscreenOpen(true);
  };

  const handleCloseFullscreen = () => {
    setFullscreenOpen(false);
  };

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="subtitle2">{title}</Typography>
          <Box>
            <Tooltip title="Copy to clipboard">
              <IconButton size="small" onClick={copyToClipboard}>
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open in fullscreen">
              <IconButton size="small" onClick={handleFullscreen}>
                <FullscreenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            bgcolor: isDark ? 'grey.900' : 'grey.50',
            maxHeight: maxHeight,
            overflow: 'auto',
            resize: 'vertical',
            minHeight: '100px',
          }}
        >
          <Typography
            variant="body2"
            component="pre"
            sx={{
              whiteSpace: 'pre-wrap',
              m: 0,
              fontFamily: format === 'json' ? 'monospace' : 'inherit',
            }}
          >
            {content}
          </Typography>
        </Paper>
      </Box>

      {/* Fullscreen Dialog */}
      <Dialog
        open={fullscreenOpen}
        onClose={handleCloseFullscreen}
        maxWidth={false}
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: isMobile ? {} : {
            width: '95vw',
            height: '95vh',
            maxWidth: '95vw',
            maxHeight: '95vh',
          },
        }}
      >
        <DialogTitle sx={{ py: isMobile ? 1.5 : 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant={isMobile ? 'subtitle1' : 'h6'}>{title}</Typography>
            <Box>
              <Tooltip title="Copy to clipboard">
                <IconButton size="small" onClick={copyToClipboard}>
                  <CopyIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close fullscreen">
                <IconButton size="small" onClick={handleCloseFullscreen}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Paper
            variant="outlined"
            sx={{
              p: isMobile ? 1.5 : 2,
              bgcolor: isDark ? 'grey.900' : 'grey.50',
              height: '100%',
              overflow: 'auto',
              borderRadius: 0,
            }}
          >
            <Typography
              variant="body2"
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                m: 0,
                fontFamily: format === 'json' ? 'monospace' : 'inherit',
                fontSize: isMobile ? '0.75rem' : '0.875rem',
              }}
            >
              {content}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ 
          px: isMobile ? 2 : 3, 
          py: isMobile ? 1.5 : 2,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0,
          '& > button': { width: isMobile ? '100%' : 'auto' }
        }}>
          <Button onClick={copyToClipboard} startIcon={<CopyIcon />}>
            {isMobile ? 'Copy' : 'Copy to Clipboard'}
          </Button>
          <Button onClick={handleCloseFullscreen}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FullscreenTextArea;
